// frontend/src/pages/builder/FiltersPanel.jsx
// Phase 3 step 8: the Data panel's Filters section. One card per filter --
// the field, how to filter (operators follow the field's type), the value --
// the way Power BI's filter cards work. Add one from the picker or by
// dragging a field from the list onto the section. A filter that is not
// complete yet shows what it needs and is not sent; the chart only ever
// gets filters the server will accept. Rules live in filters.js.
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { PanelHint, PanelLabel } from '@/components/ui/panel.jsx';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select.jsx';
import { groupFields, typeMarker } from './builderSpec';
import {
  MAX_FILTERS, MAX_VALUE_LENGTH, RELATIVE_PRESETS, fieldKind, filterProblem, filterSummary, filterableFields, operatorsFor,
  setOperator, valueShape,
} from './filters';
import { FIELD_MIME } from './Wells';

const INPUT = 'h-8 w-full rounded-lg bg-[var(--ui-panel)] px-2.5 text-[12.5px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';

function ValueControl({ filter, field, onChange }) {
  const kind = fieldKind(field);
  const shape = valueShape(filter.op);
  const label = filterSummary(filter, field);
  if (shape === 0) return null;
  if (filter.op === 'relative') {
    return (
      <Select value={filter.value} onValueChange={(v) => onChange(v)}>
        <SelectTrigger aria-label={`Period for ${field.display_name}`} className="h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {RELATIVE_PRESETS.map(([id, text]) => <SelectItem key={id} value={id}>{text}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (kind === 'boolean') {
    return (
      <Select value={String(filter.value)} onValueChange={(v) => onChange(v)}>
        <SelectTrigger aria-label={`Value for ${field.display_name}`} className="h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  const type = kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text';
  const common = { className: INPUT, maxLength: MAX_VALUE_LENGTH, ...(kind === 'number' ? { step: 'any' } : {}) };
  if (shape === 2) {
    const [a, b] = Array.isArray(filter.value) ? filter.value : ['', ''];
    return (
      <div className="grid grid-cols-2 gap-1.5">
        <input {...common} type={type} value={a} aria-label={`From, ${label}`} onChange={(e) => onChange([e.target.value, b])} />
        <input {...common} type={type} value={b} aria-label={`To, ${label}`} onChange={(e) => onChange([a, e.target.value])} />
      </div>
    );
  }
  if (shape === 'list') {
    return (
      <textarea
        className={`${INPUT} h-auto min-h-[56px] resize-y py-1.5`} rows={2} value={filter.value || ''}
        aria-label={`Values for ${field.display_name}`} placeholder="One per line, or separated by commas"
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input {...common} type={type} value={filter.value || ''} aria-label={`Value for ${field.display_name}`}
      placeholder={kind === 'number' ? 'Number' : kind === 'date' ? '' : 'Value'} onChange={(e) => onChange(e.target.value)} />
  );
}

function FilterCard({ filter, field, onChange, onRemove }) {
  const problem = filterProblem(filter, field);
  const name = (field && field.display_name) || filter.field;
  return (
    <div className="rounded-xl bg-[var(--ui-inset)] p-2.5" role="group" aria-label={filterSummary(filter, field)}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-[13px] text-[var(--ui-text)]">
          {field && <span className="w-[18px] shrink-0 font-mono text-[10px] text-[var(--ui-text-muted)]" aria-hidden="true">{typeMarker(field)}</span>}
          <span className="truncate">{name}</span>
        </span>
        <button type="button" onClick={onRemove} aria-label={`Remove filter on ${name}`}
          className="shrink-0 rounded-full p-0.5 text-[var(--ui-text-muted)] hover:bg-white/10 hover:text-[var(--ui-text)]">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      {field ? (
        <div className="flex flex-col gap-1.5">
          <Select value={filter.op} onValueChange={(op) => onChange(setOperator(filter, field, op))}>
            <SelectTrigger aria-label={`How to filter ${name}`} className="h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {operatorsFor(field).map(([op, text]) => <SelectItem key={op} value={op}>{text}</SelectItem>)}
            </SelectContent>
          </Select>
          <ValueControl filter={filter} field={field} onChange={(value) => onChange({ ...filter, value })} />
        </div>
      ) : null}
      {problem && <PanelHint className="mt-1.5 text-[var(--ui-text-muted)]">{problem} Not applied yet.</PanelHint>}
    </div>
  );
}

export default function FiltersPanel({ filters, fields, fieldsByKey, onAdd, onChange, onRemove }) {
  const [over, setOver] = useState(false);
  const groups = groupFields(filterableFields(fields));      // saved measures are not offered (13c-3)
  const full = filters.length >= MAX_FILTERS;
  const applied = filters.filter((f) => !filterProblem(f, fieldsByKey[f.field])).length;
  return (
    <div
      onDragOver={(e) => { if (Array.from(e.dataTransfer.types || []).includes(FIELD_MIME)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const key = e.dataTransfer.getData(FIELD_MIME);
        if (key) { e.preventDefault(); onAdd(key); }
      }}
      className={`-mx-2 rounded-xl px-2 pb-1 transition-[box-shadow] ${over ? 'shadow-[inset_0_0_0_1.5px_var(--ui-selected)]' : ''}`}
    >
      <PanelLabel id="builder-filters-label">
        Filters{filters.length ? ` · ${applied} of ${filters.length} applied` : ''}
      </PanelLabel>
      {filters.length > 0 && (
        <div className="mb-2 flex flex-col gap-1.5">
          {filters.map((f) => (
            <FilterCard key={f.id} filter={f} field={fieldsByKey[f.field]} onChange={(next) => onChange(f.id, next)} onRemove={() => onRemove(f.id)} />
          ))}
        </div>
      )}
      <Select value="" onValueChange={(key) => key && onAdd(key)} disabled={full}>
        <SelectTrigger aria-labelledby="builder-filters-label" aria-label="Add a filter">
          <SelectValue placeholder={full ? `Up to ${MAX_FILTERS} filters` : 'Add a filter on…'} />
        </SelectTrigger>
        <SelectContent>
          {groups.map((g) => (
            <SelectGroup key={g.role}>
              <SelectLabel>{g.label}</SelectLabel>
              {g.fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.display_name}</SelectItem>)}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      <PanelHint>{filters.length ? 'Filters apply to every row before the chart is built.' : 'Or drag a field here.'}</PanelHint>
    </div>
  );
}
